import React from 'react';
import { useImpressionRef } from '@noths/polaris-client-react-hooks';

import { PartnerProfile } from './PartnerProfile';
import type { PartnerBlockProps } from './types';
import * as styles from './PartnerBlock.styles';

/**
 * The "Meet the makers" block: an introduction, then a panel of partner profiles,
 * each with a portrait, a few words, a link to their storefront and two of their
 * products.
 *
 * Impressions are reported at two levels because the tracking asks two different
 * questions: `onVisible` fires once for the block, and `onProductVisible` fires per
 * partner when that partner's products are reached, carrying every product in one
 * event rather than one event per card.
 */
export const PartnerBlock = ({
  copy,
  heading,
  lazyImages,
  onPartnerClick,
  onProductClick,
  onProductVisible = () => {},
  onVisible = () => {},
  partners,
}: PartnerBlockProps) => {
  const partnersRef = useImpressionRef<HTMLElement>(() => onVisible());

  return (
    <section css={styles.block} ref={partnersRef}>
      <header css={styles.header}>
        <h2 css={styles.heading}>{heading}</h2>
        <p css={styles.copy}>{copy}</p>
      </header>
      <div css={styles.panel}>
        <ul css={styles.partners}>
          {partners.map((partner, partnerIndex) => (
            <li css={styles.partnerItem} key={partner.url}>
              <PartnerProfile
                {...partner}
                lazyImages={lazyImages}
                onPartnerClick={(e) => onPartnerClick?.(e, { partnerIndex })}
                onProductClick={(e, { productIndex }) =>
                  onProductClick?.(e, { partnerIndex, productIndex })
                }
                onVisible={() => onProductVisible({ partnerIndex })}
              />
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
};

PartnerBlock.displayName = 'PartnerBlock';
